import { PermissionEnum } from '@sr-sdks/api-sdk-axios';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { userService } from '../reducers/permissions/userSlice';
import { useTypedSelector } from '../stateStore';
import { AuthHelper } from '../utils/auth/authHelper';
import { isLoadingState } from '../utils/helpers/common';
import ErrorPage from './ErrorPage';
import { AppLoader } from './Loader';

export interface IProtectElementProps {
  children: ReactNode;
  onAuthorised?: () => void;
  permissionRequired?: PermissionEnum[] | null;
  showErrorPage?: boolean;
  workspaceId?: string;
}

const ProtectElement = ({
  children,
  onAuthorised,
  permissionRequired = [],
  showErrorPage = false,
  workspaceId,
}: IProtectElementProps) => {
  const userMe = useTypedSelector(userService.selectors.userMe);
  const isUserMeLoading = useTypedSelector(userService.selectors.isLoading);
  const location = useLocation();

  const isAuthorized = useMemo(() => {
    // Null means no user profile or permissions are required
    if (permissionRequired === null) return true;

    // User profile required
    if (!userMe) return false;

    // Empty array means no specific permission is required
    if (permissionRequired.length === 0) return true;

    const auth = new AuthHelper(userMe);

    if (workspaceId) {
      return auth.currentUserHasPermissionOnWorkspace(
        workspaceId,
        ...permissionRequired,
      );
    }

    return auth.currentUserHasPermission(...permissionRequired);
  }, [permissionRequired, userMe, workspaceId, location]);

  // Callers pass a new arrow every render, so key the call on the workspace
  // instead: once per authorisation, not on every navigation or findMe reload.
  const authorisedForRef = useRef<null | string>(null);
  const isUserMeSettled = !isLoadingState(isUserMeLoading);

  useEffect(() => {
    if (!isAuthorized) {
      authorisedForRef.current = null;

      return;
    }

    const authorisedFor = workspaceId ?? '';

    if (!isUserMeSettled || authorisedForRef.current === authorisedFor) return;

    authorisedForRef.current = authorisedFor;
    onAuthorised?.();
  }, [isAuthorized, isUserMeSettled, onAuthorised, workspaceId]);

  if (isAuthorized) return children;

  if (isLoadingState(isUserMeLoading))
    return <AppLoader.FlexLoader loaderHeight="h-screen" />;

  if (showErrorPage)
    return (
      <ErrorPage
        details={{
          path: location.pathname + location.search,
          requiredPermissions: permissionRequired ?? [],
          workspaceId,
        }}
        notAuthorized
      />
    );

  return <> </>;
};

export default ProtectElement;
